import { IProfileResponse } from "services/profile";
import { Item } from "./Item";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  addItemToCardService,
  clearCartService,
  fetchCardItemsService,
  removeCardItemService,
} from "services/cart";
import { getCartId } from "utils/getCartId";
import {
  addFavoriteService,
  deleteFavoriteService,
  favoriteService,
} from "services/favorite";
import { materialTypeService } from "services/materialType";
import { IResultCalculator, calculatorService } from "services/calculator";
import { getUserId } from "utils/getUserId";
import { useForm } from "react-hook-form";
import { ConfirmModal } from "components/ConfirmModal";
import { CalculatorContext } from "App";

interface IProps {
  profiles: IProfileResponse[] | null;
}
interface IOption {
  label: string;
  value: string;
  default?: boolean;
}

export const CalculatedItems = ({ profiles }: IProps) => {
  const [profileType, setProfileType] = useState<string | null>(null);
  const [resultCalculator, setResultCalculator] = useState<IResultCalculator>();
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [inputValues, setInputValues] = useState<Record<string, number>>({});
  const [isFavorite, setIsFavorite] = useState(false); // Track whether the item is in favorites or not
  const [isLoading, setIsLoading] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [materials, setMaterials] = useState<IOption[]>([]);
  const [densities, setDensities] = useState<IOption[]>([]);
  const [favorites, setFavorites] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [removedItemId, setRemovedItemId] = useState("");
  const [isOpenClearModalm, setIsOpenClearModal] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [itemAddedToCart, setItemAddedToCart] = useState(false);
  // const [selectedDensity, setSelectedDensity] = useState("");
  const [incorrectInputValues, setIncorrectInputValues] = useState(false);
  const [isAddingToFavorite, setIsAddingToFavorite] = useState(false);
  const [foundFavorite, setFoundFavorite] = useState<any>();

  const userId = getUserId();
  const cartId = getCartId();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    setValue,
    watch,
    trigger,
  } = useForm();

  const { a, b, d, L, h, w, density, quantity } = watch();

  useEffect(() => {
    const firstProfile = profiles[0]?.name;
    if (firstProfile) setProfileType(firstProfile);
  }, [profiles]);

  const generateDefaultValue = () => {
    const defualtValues = profiles.map((profile) => {
      if (profileType === profile.profileType) {
        return profile.calculationFields.map((item) => ({
          [item.shortName]: item.defautValue,
        }));
      }
    });
    const defaultValueObject = defualtValues.find((item) => item);
    if (defaultValueObject) {
      defaultValueObject.map((item) => {
        for (const [key, value] of Object.entries(item)) {
          setValue(key, value);
          reset({ [key]: value });
        }
      });
    }
  };

  useEffect(() => {
    generateDefaultValue();
  }, [profiles, profileType]);

  const handleChangeProfileType = (data: string) => {
    if (profileType === data) {
      return;
    }

    setValue("quantity", 1);

    setIsFavorite(false);
    setIsAddingToFavorite(false);
    setProfileType(data);

    // const defaultDensity = densities.find((density) => density.default)?.value;
    // if (defaultDensity) {
    //   setValue("density", defaultDensity);
    // }

    setResultCalculator({
      ...resultCalculator,
      weight: {
        additionPost: false,
        additionPre: false,
        factor: false,
        label: "",
        symbol: "",
        value: 0,
      },
      length: {
        additionPost: false,
        additionPre: false,
        factor: 0,
        label: "",
        symbol: "",
        value: 0,
      },
    });
  };

  const handleSelect = (val) => {
    setSelectedMaterial(val);
    // setSelectedDensity("");
  };

  const onsubmit = async (data: any) => {
    const valuesOfObject = Object.values(data);

    if (valuesOfObject.length <= 1) return;
    if (valuesOfObject.includes(undefined)) return;
    if (valuesOfObject.includes("")) return;
    if (!density) return;

    setIsLoading(true);
    const measure = [];
    for (const [key, value] of Object.entries(data)) {
      if (value) {
        measure.push({ name: key, value: Number(value) });
      }
    }

    const removedQuantity = measure.filter(
      (item) => item.name !== "quantity" && item.name !== "density"
    );

    const payload = {
      profileType,
      measure: removedQuantity,
      density,
      quantity: data.quantity ? Number(data.quantity) : 1,
    };

    calculateDataSubmit(payload);
  };

  const calculateDataSubmit = async (payload: any) => {
    const response = await calculatorService(payload);
    if (response.status === 200) {
      setIsLoading(false);
      setResultCalculator(response.data.responseData);
      // Check if the calculated weight is negative
      if (response.data.responseData.weight.value < 0) {
        setIncorrectInputValues(true);
      } else {
        setIncorrectInputValues(false);
      }
      return;
    }
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      onsubmit(watch());
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [a, b, h, d, L, w, density, quantity, inputValues]); // Add inputValues to the dependency array

  const fetchDataFromFavoriteList = async (event, data) => {
    event.stopPropagation();

    // Check if the picked profile is in the favorites array
    const isProfileInFavorites = favorites.some((favorite) => {
      const favoriteFilledFields = favorite.filledFields;
      const dataFilledFields = data.filledFields;

      // Compare the count of shortName's and values
      if (favoriteFilledFields.length !== dataFilledFields.length) {
        return false;
      }

      // Compare each item's shortName and value
      for (let i = 0; i < favoriteFilledFields.length; i++) {
        if (
          favoriteFilledFields[i].shortName !== dataFilledFields[i].shortName ||
          favoriteFilledFields[i].value !== dataFilledFields[i].value
        ) {
          return false;
        }
      }

      return true;
    });

    setIsFavorite(isProfileInFavorites);

    handleChangeProfileType(data.profile.name);

    const payloadFields = {};
    const newInputValues = {};

    for (const val of data.filledFields) {
      payloadFields[val.shortName] = val.value;
      newInputValues[val.shortName] = val.value;
    }

    setProfileType(data.profile.profileType);
    setRemovedItemId(data.id);

    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      ...newInputValues,
      density:
        data.filledFields.find((item) => item.shortName === "density")?.value ||
        "",
    }));

    // Trigger validation after setting input values
    await trigger();

    const measure = data.filledFields.map((item) => ({
      name: item.shortName,
      value: item.value,
    }));

    const payload = {
      profileType,
      measure,
      density: densities.find((item) => item.default).value,
      quantity: data.quantity ? Number(data.quantity) : 1,
    };

    // Wait for validation to complete and then submit the form
    calculateDataSubmit(payload);
    // await handleSubmit(onsubmit);
    setTimeout(() => {
      reset({ ...payloadFields, quantity: 1 });
    });
  };

  useEffect(() => {
    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      ...watch(),
    }));

    handleInputChange();
  }, [a, b, h, d, L, w, density, quantity]);

  const fetchFavoriteProfiles = async () => {
    if (!userId) return;
    const response = await favoriteService(userId);
    setFavorites(response.data.items);
  };

  const fetchMaterialType = () => {
    materialTypeService().then((response) => {
      if (response.status === 200) {
        const option = response.data.map((item) => {
          if (selectedMaterial === item.slug) {
            const densitieOptions = [];
            item.densities.map((densitie) => {
              densitieOptions.push({
                value: densitie.density,
                label: densitie.density,
                default: densitie.default,
              });
            });
            setDensities(densitieOptions);
          }

          return {
            label: item.name,
            value: item.slug,
            default: item.default,
          };
        });

        setMaterials(option);
      }
    });
  };

  const addToFavorite = async () => {
    const getValue = getValues();
    const filledFields = [];
    profiles.map((profile) => {
      if (profileType === profile.profileType) {
        profile.calculationFields.map((field) => {
          for (const value of Object.keys(getValue)) {
            if (field.shortName === value) {
              filledFields.push({
                name: field.name,
                shortName: value,
                value: getValue[value],
              });
            }
          }
        });
      }
    });

    const foundNullableValue = filledFields.find((item) => !item.value);

    if (foundNullableValue) return;

    const favoriteItem = favorites.find((item) => {
      const favoriteFilledFields = item.filledFields;

      // Compare the count of shortName's and values
      if (favoriteFilledFields.length !== filledFields.length) {
        return false;
      }

      // Compare each item's shortName and value
      for (let i = 0; i < favoriteFilledFields.length; i++) {
        if (
          favoriteFilledFields[i].shortName !== filledFields[i].shortName ||
          favoriteFilledFields[i].value !== filledFields[i].value
        ) {
          return false;
        }
      }

      return true;
    });

    if (favoriteItem) {
      // Remove from favorites if already in favorites
      handleRemoveFromFavourite(favoriteItem.id);
    } else {
      // Add to favorites if not in favorites
      const payload = {
        profileType,
        filledFields,
        popularId: "",
      };
      await addFavoriteService(userId, payload);
      fetchFavoriteProfiles();
      setIsFavorite(true);
    }

    openAlert();
  };

  const handleRemoveFromFavourite = async (id) => {
    await deleteFavoriteService(id);
    fetchFavoriteProfiles();
    setIsFavorite(false);
  };

  const handleToggleFavoriteItem = async (isFav: boolean) => {
    setIsAddingToFavorite(true);
    if (!isFav) {
      await addToFavorite();
    } else {
      if (foundFavorite)
        await handleRemoveFromFavourite(`${foundFavorite.id!}`);
    }

    setIsAddingToFavorite(false);
  };

  const context = useContext(CalculatorContext);

  const { handleItemCount, translations } = context!;

  const fetchCardItems = async () => {
    const response = await fetchCardItemsService(getCartId());
    setCartItems(response.data.items);
    handleItemCount(response.data.items.length);
  };

  const addItemToCard = async () => {
    const getValue = getValues();
    const filledFields = [];
    profiles.map((profile) => {
      if (profileType === profile.profileType) {
        profile.calculationFields.map((field) => {
          for (const value of Object.keys(getValue)) {
            if (field.shortName === value) {
              filledFields.push({
                name: field.name,
                shortName: value,
                value: getValue[value],
              });
            }
          }
        });
      }
    });

    const foundNullableValue = filledFields.find((item) => !item.value);

    if (foundNullableValue) return;

    const payload = {
      profileType,
      quantity,
      density,
      filledFields,
    };

    const response = await addItemToCardService(cartId, payload);
    if (response.status === 200) {
      setItemAddedToCart(true); // Ustawienie stanu na true po dodaniu do koszyka
      fetchCardItems();
    }
  };

  const handleRemoveItem = async () => {
    const response = await removeCardItemService(removedItemId);
    if (response.status === 200) {
      setIsOpen(false);
      fetchCardItems();
    }
  };

  const handleClearCart = async () => {
    const response = await clearCartService(getCartId());
    if (response.status === 200) {
      setIsOpenClearModal(false);
      fetchCardItems();
    }
  };

  const handleOpenClearConfirmModal = (id: string) => {
    if (cartItems.length === 0) return;

    setIsOpenClearModal(true);
    setRemovedItemId(id);
  };

  const handleOpenConfirmModal = (id: string) => {
    setIsOpen(true);
    setRemovedItemId(id);
  };

  useEffect(() => {
    fetchMaterialType();
  }, [selectedMaterial]);

  useEffect(() => {
    fetchCardItems();
  }, []);

  useEffect(() => {
    fetchFavoriteProfiles();
    reset({ quantity: 1 });
  }, []);

  useEffect(() => {
    const mainContainer = document.querySelector("#root main");
    const divsWithSpecialClass =
      mainContainer.querySelectorAll("#root>main>div");
    if (divsWithSpecialClass.length === 7) {
      mainContainer.classList.add("special-margin");
    } else if (divsWithSpecialClass.length !== 7) {
      mainContainer.classList.remove("special-margin");
    }
  });
  const handleInputChange = () => {
    const inputValues: Record<string, number> = {}; // Store input values for each field name

    // Collect input values for each field name
    document.querySelectorAll("input[name]").forEach((input) => {
      const fieldName = input.getAttribute("name");
      const inputValue = parseFloat((input as HTMLInputElement).value); // Type assertion
      if (!isNaN(inputValue)) {
        inputValues[fieldName] = inputValue;
      }
    });

    // Check if there is a matching favorite for all input values
    const isMatchingFavorite = favorites.some((favorite) => {
      return favorite.filledFields.every((field) => {
        if (
          inputValues[field.shortName] !== undefined &&
          parseFloat(field.value) === inputValues[field.shortName]
        ) {
          setFoundFavorite(favorite);
          return true;
        } else {
          return false;
        }
      });
    });
    setIsFavorite(isMatchingFavorite);
  };

  const openAlert = useCallback(() => {
    setAlertOpen(true);
  }, []);

  const closeAlert = () => {
    setAlertOpen(false);
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        closeAlert();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertOpen]);

  useEffect(() => {
    if (itemAddedToCart) {
      reset(); // Zresetowanie pól formularza
      setResultCalculator(undefined); // Wyzerowanie resultCalculator
      setItemAddedToCart(false); // Ustawienie stanu na false
    }
  }, [itemAddedToCart]);

  const activeProfile = profiles.find(
    (item) => item.profileType === profileType
  );

  useEffect(() => {
    const defaultDensity = densities.find((density) => density.default)?.value;
    if (defaultDensity) {
      setValue("density", defaultDensity);
    }
  }, [profileType]);

  return (
    <>
      <Item
        items={cartItems}
        handleRemove={handleOpenConfirmModal}
        handleClear={handleOpenClearConfirmModal}
      />

      <ConfirmModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        handleSubmit={handleRemoveItem}
        modalText={`${translations["remove-item-from-basket"]}`}
      />
      <ConfirmModal
        isOpen={isOpenClearModalm}
        handleClose={() => setIsOpenClearModal(false)}
        handleSubmit={handleClearCart}
        modalText={`${translations["remove-all-items-from-basket"]}`}
      />
    </>
  );
};
